import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { SystemRoles } from '@/client/users';
import { learnerOnlyLanguages } from '@/common/constants/languages';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { isThereTranslation } from '@/utils/helpers';
import DateHolidays from 'date-holidays';
import { Calendar, CalendarBaseProps } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import 'react-time-picker/dist/TimePicker.css';
import styled from 'styled-components';
import { TimeControls } from './TimeControls';
import { calculateNextValidTime } from './utils';

const StyledCalendar = styled(Calendar)`
  &.p-calendar {
    &.p-calendar-w-btn.p-calendar-w-btn-left {
      .p-button {
        background-color: var(--beige-main);
        color: var(--black-main);
        border: none;
        border-start-start-radius: var(--default-border-radius);
        border-bottom-left-radius: var(--default-border-radius);
        padding-left: var(--small-padding);
        span {
          font-size: var(--medium-font-size);
        }
      }

      .p-inputtext {
        border-bottom-left-radius: 0 !important;
        border-top-left-radius: 0 !important;
      }
    }
  }

  &.white-input {
    .p-inputtext,
    .p-button {
      background-color: var(--white-main) !important;
      border-color: var(--white-main);
    }
  }
`;

type AppCalendarWithCustomTimepickerProps = Omit<
  CalendarBaseProps,
  'onChange' | 'value'
> & {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  dateFormat?: string;
  className?: string;
  withHolidays?: boolean;
};

export const AppCalendarWithCustomTimepicker = ({
  dateFormat = 'M dd, yy,',
  withHolidays = false,
  value,
  onChange,
  ...rest
}: AppCalendarWithCustomTimepickerProps) => {
  const user = useAppSelector(selectCurrentUser);
  const account = useAppSelector(selectCurrentAccount);
  const isDanishLanguage = account?.defaultLanguage === LanguagesEnum.DK;
  const isSuperAdmin = user?.role.code === SystemRoles.SUPER_ADMIN;
  const holidaysEnabled = withHolidays && (isDanishLanguage || isSuperAdmin);

  const holidays = React.useMemo(() => {
    if (!holidaysEnabled) return null;

    const hd = new DateHolidays(languageDisplayCode[LanguagesEnum.DK], {
      types: ['public'],
    });
    return hd;
  }, [holidaysEnabled]);

  const holidayTemplate = (date: {
    day: number;
    month: number;
    year: number;
  }) => {
    if (!holidaysEnabled || !holidays) return date.day;

    const jsDate = new Date(date.year, date.month, date.day);
    const holidayInfo = holidays.isHoliday(jsDate);
    if (!Array.isArray(holidayInfo) || holidayInfo.length === 0)
      return date.day;

    const holidayId = `holiday-${date.year}-${date.month}-${date.day}`;
    return (
      <>
        <div id={holidayId} className="holiday">
          {date.day}
        </div>
        <Tooltip
          target={`span:has(#${holidayId})`}
          content={holidayInfo[0].name}
        />
      </>
    );
  };

  const handleDateChange = (e: { value: any }) => {
    const newDate = e.value ? (e.value as Date) : undefined;

    if (!newDate) {
      onChange?.(null);
      return;
    }

    const stepMinute = rest?.stepMinute || 1;
    const adjustedTime = calculateNextValidTime(
      newDate.getHours(),
      newDate.getMinutes(),
      stepMinute,
    );

    newDate.setHours(adjustedTime.hours);
    newDate.setMinutes(adjustedTime.minutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    onChange?.(newDate);
  };

  const handleTimeChange = (newValue: Date | null) => {
    onChange?.(newValue);
  };

  const footerContent = () => (
    <TimeControls
      value={value || null}
      onChange={handleTimeChange}
      stepMinute={rest?.stepMinute}
      minDate={rest?.minDate}
    />
  );

  return (
    <StyledCalendar
      className={rest.className}
      value={value}
      onChange={handleDateChange}
      locale={
        isThereTranslation() &&
        !learnerOnlyLanguages.includes(user?.language as LanguagesEnum)
          ? user?.language
          : LanguagesEnum.EN
      }
      dateFormat={dateFormat}
      dateTemplate={holidaysEnabled ? holidayTemplate : undefined}
      footerTemplate={footerContent}
      selectionMode="single"
      panelClassName={
        'custom-timepicker' + (holidaysEnabled ? ' holiday-datepicker' : '')
      }
      hourFormat="24"
      {...rest}
    />
  );
};
